/**
 * Accounts one referral code brought in.
 *
 * The list is the attribution record: who registered, through which channel, and
 * when the binding was written. Bindings are never revised, so this page is a
 * history rather than a current state that can change under the reader.
 */

import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { LoadingBlock, ErrorBlock } from '../../components/layout/states'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { EmptyState } from '../../components/ui/primitives'
import { useReferralSignups } from '../../hooks/use-admin'
import { formatUnixSeconds } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { AdminPage, Pager, StateBadge, usePageParam } from './shared'

export function AdminReferralDetailPage() {
  const { t } = useTranslation()
  const { code = '' } = useParams()
  const { page, goToPage } = usePageParam()
  const locale = currentLocale()

  const signups = useReferralSignups(code, page)

  return (
    <AdminPage
      title={t('admin.referralDetail.title', { code })}
      current={t('admin.nav.referrals')}
    >
      {signups.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {signups.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void signups.refetch()}
        />
      ) : null}
      {signups.data && signups.data.items.length === 0 ? (
        <EmptyState title={t('admin.referralDetail.empty')} />
      ) : null}

      <div className="flex flex-col gap-3">
        {signups.data?.items.map((item) => (
          <Card key={item.userId}>
            <CardContent className="flex flex-col gap-1 pt-4 sm:pt-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm">{item.email}</span>
                {item.channel ? (
                  <StateBadge
                    label={t(`admin.referralChannel.${item.channel}`)}
                    variant={item.channel === 'direct' ? 'outline' : 'default'}
                  />
                ) : null}
              </div>
              <span className="text-muted-foreground text-xs">
                {item.nickname ?? t('admin.users.nicknameEmpty')} ·{' '}
                {item.boundAt === null
                  ? '—'
                  : t('admin.referralDetail.boundAt', {
                      time: formatUnixSeconds(item.boundAt, locale),
                    })}
              </span>
            </CardContent>
          </Card>
        ))}
      </div>

      {signups.data ? (
        <Pager
          page={signups.data.page}
          pageSize={signups.data.pageSize}
          total={signups.data.total}
          onPage={goToPage}
        />
      ) : null}

      <div>
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/referrals">{t('admin.referrals.back')}</Link>
        </Button>
      </div>
    </AdminPage>
  )
}
